import { Download, ArrowLeft, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { IssueCard } from '../issues/IssueCard';
import type { Issue } from '@/types/reportApp';
import type { ReportStatus } from '@/types/report';

interface PreQCReportScreenProps {
  packageName: string;
  reportId: string;
  issues: Issue[];
  isLoadingReport: boolean;
  reportStatus?: ReportStatus;
  onHandleDownloadPDF: () => void;
  onBack?: () => void;
}

// Helper function to get status badge styling
const getStatusBadgeProps = (status: ReportStatus) => {
  switch (status) {
    case 'incoming':
      return { label: 'Incoming', className: 'bg-gray-100 text-gray-700' };
    case 'preqc':
      return { label: 'Pre-QC Failed', className: 'bg-red-100 text-red-700' };
    case 'qc-pending':
      return { label: 'QC Pending', className: 'bg-yellow-100 text-yellow-700' };
    case 'qc-reviewed':
      return { label: 'QC Reviewed', className: 'bg-green-100 text-green-700' };
    default:
      return { label: status, className: 'bg-gray-100 text-gray-700' };
  }
};

export const PreQCReportScreen = ({
  packageName,
  reportId,
  isLoadingReport,
  reportStatus,
  issues,
  onHandleDownloadPDF,
  onBack,
}: PreQCReportScreenProps) => {
  return (
    <div className="min-h-screen">
      <div className="p-6 pb-24">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Loading State */}
          {isLoadingReport ? (
            <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-12 text-center">
              <div className="animate-spin w-16 h-16 border-4 border-wppBlue border-t-transparent rounded-full mx-auto mb-6"></div>
              <h2 className="text-2xl font-semibold mb-2">Loading Report...</h2>
              <p className="text-gray-600">Fetching report data...</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Back Button */}
                  {onBack && (
                    <button
                      onClick={onBack}
                      className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span className="font-medium">Back</span>
                    </button>
                  )}

                  <button
                    onClick={onHandleDownloadPDF}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-wppBlue hover:text-white transition-all"
                  >
                    <Download className="w-4 h-4 inline mr-2" />
                    Download PDF
                  </button>
                </div>
              </div>

              {/* Report Header */}
              <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold mb-2">Pre-QC Report</h1>
                    <div className="space-y-1">
                      <p className="text-gray-600">Ford Puma Ready for Anything Campaign</p>
                      {packageName && (
                        <p className="text-sm text-gray-400">
                          <span className="text-gray-400">Package:</span>{' '}
                          <span className="font-mono font-semibold text-blue-600">{packageName}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-400 mb-1">Report ID</p>
                    <p className="font-mono font-semibold text-lg text-blue-600 mb-2">
                      {reportId || 'N/A'}
                    </p>
                    {reportStatus && (
                      <Badge className={getStatusBadgeProps(reportStatus).className}>
                        {getStatusBadgeProps(reportStatus).label}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Pre-QC Status Box */}
              <div className="bg-red-50 border-2 border-red-200 shadow-sm rounded-2xl p-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="w-8 h-8 text-red-600" />
                  </div>
                  <h2 className="text-2xl font-semibold text-red-900 mb-2">Pre-QC Issues Found</h2>
                  <p className="text-red-700">
                    {issues.length} issue{issues.length === 1 ? '' : 's'} found that must be fixed before Full QC
                  </p>
                </div>
              </div>

              {/* Issues List */}
              {issues.length > 0 && (
                <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-6">
                  <h2 className="text-xl font-semibold mb-4">
                    Issues Found ({issues.length})
                  </h2>
                  <div className="space-y-2">
                    {issues.map((issue) => (
                      <IssueCard
                        key={issue.id}
                        issue={issue}
                        isEditing={false}
                        readOnly={true}
                        onEdit={() => {}}
                        onCancelEdit={() => {}}
                        onUpdate={() => {}}
                        onApprove={() => {}}
                        onMoveToPending={() => {}}
                        onDismiss={() => {}}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
